/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useRef, useEffect, useState } from "react";

type UseElementHeightOptions = {
  deps?: unknown[];
  condition?: boolean;
};

/**
 * Custom hook to measure the height of a DOM element
 *
 * @param options - Configuration object
 * @param options.deps - Optional array of dependencies that trigger height recalculation
 * @param options.condition - Optional condition that must be true to measure height
 * @returns Object containing ref to attach to element and current height
 *
 * @example
 * const { ref, height } = useElementHeight({
 *   deps: [content, isVisible],
 *   condition: isVisible
 * });
 *
 * return <div ref={ref}>Content here</div>;
 */
export function useElementHeight(options: UseElementHeightOptions = {}) {
  const { deps = [], condition = true } = options;
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (condition && ref.current) {
      setHeight(ref.current.offsetHeight);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, condition]);

  return { ref, height };
}
